import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import GuarantorPickerModal, {
    IGuarantorOption,
} from 'components/modals/guarantorPicker'
import { getLoanGuarantors, setLoanGuarantors } from 'services/guarantees'

/**
 * ضامن‌های یک وام, on the loan-detail screen.
 *
 * This is the path that lets managers fill in the ~33.8k loans that predate the
 * feature — nothing was backfilled automatically, deliberately, so that nobody
 * shows up as a guarantor for a debt they never agreed to.
 *
 * Members see the row read-only; only managers get the edit control.
 */
export default function LoanGuarantors({
    moneypoolId,
    loanId,
    borrowerPoolshipId,
    isManager,
}: {
    moneypoolId?: string
    loanId?: string
    borrowerPoolshipId?: number
    isManager: boolean
}) {
    const [isPickerOpen, setIsPickerOpen] = useState(false)
    const queryClient = useQueryClient()

    const { data } = useQuery(
        ['loan-guarantors', moneypoolId, loanId],
        () => getLoanGuarantors(moneypoolId as string, loanId as string),
        { enabled: !!moneypoolId && !!loanId }
    )

    const guarantors: Array<IGuarantorOption> = (
        data?.results?.guarantors ?? []
    ).map((item: any) => ({
        poolship_id: item.poolship,
        name: item.name,
    }))

    const suggested: IGuarantorOption | null = data?.results?.suggested
        ? {
              poolship_id: data.results.suggested.id,
              name: `${data.results.suggested.member?.first_name ?? ''} ${
                  data.results.suggested.member?.last_name ?? ''
              }`.trim(),
          }
        : null

    const { mutate, isLoading: isSaving } = useMutation({
        mutationFn: (ids: Array<number>) =>
            setLoanGuarantors({
                moneypoolId: moneypoolId as string,
                loanId: loanId as string,
                guarantor_ids: ids,
            }),
        onSuccess: (_response, ids) => {
            posthog.capture('loan_guarantors_saved', {
                fund_id: moneypoolId,
                loan_id: loanId,
                count: ids.length,
            })
            queryClient.invalidateQueries([
                'loan-guarantors',
                moneypoolId,
                loanId,
            ])
            // «پیگیری از ضامن‌ها» lists these same people
            queryClient.invalidateQueries(['guarantor-nudge', moneypoolId])
            toast.success('ضامن وام ثبت شد')
        },
        onError: (error: any, ids) => {
            // saves had no denominator: a refusal only ever became a toast, so
            // «ثبت ضامن» looked like it always worked
            posthog.capture('loan_guarantors_save_failed', {
                fund_id: moneypoolId,
                loan_id: loanId,
                count: ids.length,
                status: error?.response?.status,
            })
            // the backend answers 2051 with a Persian reason in `params`
            const reason = error?.response?.data?.params
            toast.error(reason || 'ثبت ضامن انجام نشد')
        },
    })

    return (
        <div className="px-4 pt-3">
            <div className="bg-white border border-divider rounded-card shadow-card px-4 py-3">
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-text-secondary shrink-0">
                        ضامن وام
                    </span>
                    <div className="text-left">
                        <span className="text-[15px] font-medium text-text-body">
                            {guarantors.length > 0
                                ? guarantors.map((item) => item.name).join('، ')
                                : 'ثبت نشده'}
                        </span>
                        {isManager && (
                            <span
                                className="block text-xs font-bold text-green-1 cursor-pointer mt-0.5"
                                onClick={() => {
                                    if (isSaving) return
                                    posthog.capture(
                                        'loan_guarantor_picker_opened',
                                        {
                                            fund_id: moneypoolId,
                                            source: 'loan_detail',
                                            has_suggested: !!suggested,
                                            current_count: guarantors.length,
                                        }
                                    )
                                    setIsPickerOpen(true)
                                }}
                            >
                                {guarantors.length > 0
                                    ? 'ویرایش ضامن'
                                    : 'افزودن ضامن'}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {isManager && (
                <GuarantorPickerModal
                    show={isPickerOpen}
                    onHide={() => setIsPickerOpen(false)}
                    moneypoolId={moneypoolId}
                    value={guarantors}
                    onChange={(selected) =>
                        mutate(selected.map((item) => item.poolship_id))
                    }
                    excludePoolshipId={borrowerPoolshipId}
                    suggested={suggested}
                />
            )}
        </div>
    )
}
